// Delivery Boys API Functions - Real Backend Integration

import apiUtils from '../../../services/api/apiUtils';
import { DeliveryBoy, DeliveryBoyFormData, DeliveryBoyStatus } from '../types';

// Get all delivery boys
export const getDeliveryBoys = async (): Promise<DeliveryBoy[]> => {
  try {
    const response = await apiUtils.get<{
      deliveryBoys: any[];
      pagination: any;
    }>('/admin/delivery-boys');

    return (response.data?.deliveryBoys || []).map(mapBackendToFrontend);
  } catch (error) {
    console.error('Failed to fetch delivery boys:', error);
    return [];
  }
};

// Get delivery boy by ID
export const getDeliveryBoyById = async (id: number): Promise<DeliveryBoy | undefined> => {
  try {
    const response = await apiUtils.get<any>(`/admin/delivery-boys/${id}`);
    return response.data ? mapBackendToFrontend(response.data) : undefined;
  } catch (error) {
    console.error('Failed to fetch delivery boy:', error);
    return undefined;
  }
};

// Create delivery boy
export const createDeliveryBoy = async (data: DeliveryBoyFormData): Promise<DeliveryBoy> => {
  const response = await apiUtils.post<any>('/admin/delivery-boys', {
    firstName: data.firstName,
    lastName: data.lastName,
    email: data.email,
    phone: data.phone,
    dateOfBirth: data.dateOfBirth,
    gender: data.gender,
    joiningDate: data.joiningDate,
    vehicleType: data.vehicleType,
    vehicleNumber: data.vehicleNumber,
    drivingLicenseNumber: data.drivingLicenseNumber,
    shiftType: data.shiftType,
    deliveryArea: data.deliveryArea,
    isAvailable: data.isAvailable,
    address: data.address,
    emergencyContactName: data.emergencyContactName,
    emergencyContactPhone: data.emergencyContactPhone,
    salaryType: data.salaryType,
    baseSalary: data.baseSalary,
    commissionRate: data.commissionRate,
    accountNumber: data.accountNumber,
    ifscCode: data.ifscCode,
    bankName: data.bankName,
    notes: data.notes,
  });

  return mapBackendToFrontend(response.data);
};

// Update delivery boy
export const updateDeliveryBoy = async (id: number, data: DeliveryBoyFormData): Promise<DeliveryBoy> => {
  const response = await apiUtils.put<any>(`/admin/delivery-boys/${id}`, {
    firstName: data.firstName,
    lastName: data.lastName,
    email: data.email,
    phone: data.phone,
    dateOfBirth: data.dateOfBirth,
    gender: data.gender,
    vehicleType: data.vehicleType,
    vehicleNumber: data.vehicleNumber,
    drivingLicenseNumber: data.drivingLicenseNumber,
    shiftType: data.shiftType,
    deliveryArea: data.deliveryArea,
    isAvailable: data.isAvailable,
    address: data.address,
    emergencyContactName: data.emergencyContactName,
    emergencyContactPhone: data.emergencyContactPhone,
    salaryType: data.salaryType,
    baseSalary: data.baseSalary,
    commissionRate: data.commissionRate,
    accountNumber: data.accountNumber,
    ifscCode: data.ifscCode,
    bankName: data.bankName,
    notes: data.notes,
  });

  return mapBackendToFrontend(response.data);
};

// Delete delivery boy
export const deleteDeliveryBoy = async (id: number): Promise<void> => {
  await apiUtils.delete(`/admin/delivery-boys/${id}`);
};

// Toggle delivery boy status
export const toggleDeliveryBoyStatus = async (id: number): Promise<DeliveryBoy> => {
  const response = await apiUtils.put<any>(`/admin/delivery-boys/${id}/status`, {});
  return mapBackendToFrontend(response.data);
};

// Reset delivery boy app password. If `newPassword` is omitted the
// backend resets to the boy's phone number — the documented default.
// Returns the plaintext that the admin should share with the boy
// (passed in by caller, since backend never echoes plaintext back).
export const resetDeliveryBoyPassword = async (
  id: number,
  newPassword?: string,
): Promise<{ success: boolean; message?: string }> => {
  try {
    const res = await apiUtils.put<any>(
      `/admin/delivery-boys/${id}/reset-password`,
      newPassword ? { newPassword } : {},
    );
    return { success: true, message: res.message || 'Password reset' };
  } catch (error: any) {
    return { success: false, message: error?.response?.data?.message || 'Failed to reset password' };
  }
};

// Map backend response to frontend DeliveryBoy type
function mapBackendToFrontend(data: any): DeliveryBoy {
  let address: any[] = [];
  try {
    address = typeof data.address === 'string' ? JSON.parse(data.address) : (data.address || []);
  } catch {
    address = [];
  }

  return {
    id: data.id,
    employeeId: data.employeeId,
    firstName: data.firstName,
    lastName: data.lastName,
    email: data.email,
    phone: data.phone,
    dateOfBirth: data.dateOfBirth || '',
    gender: data.gender || 'male',
    status: data.status === 'active' ? DeliveryBoyStatus.ACTIVE :
            data.status === 'inactive' ? DeliveryBoyStatus.INACTIVE :
            DeliveryBoyStatus.SUSPENDED,
    joiningDate: data.joiningDate || '',
    lastActiveDate: data.updatedAt,
    vehicleType: data.vehicleType || '',
    vehicleNumber: data.vehicleNumber,
    drivingLicenseNumber: data.drivingLicenseNumber,
    shiftType: data.shiftType || '',
    deliveryArea: data.deliveryArea || '',
    isAvailable: data.isAvailable ?? true,
    address,
    emergencyContactName: data.emergencyContactName || '',
    emergencyContactPhone: data.emergencyContactPhone || '',
    documents: [],
    stats: {
      totalDeliveries: data.totalDeliveries || 0,
      completedDeliveries: data.completedDeliveries || 0,
      cancelledDeliveries: data.cancelledDeliveries || 0,
      averageRating: data.averageRating || 0,
      totalEarnings: 0,
      monthlyEarnings: 0,
      onTimeDeliveryRate: 0,
    },
    salaryType: data.salaryType || 'fixed',
    baseSalary: data.baseSalary,
    commissionRate: data.commissionRate,
    accountNumber: data.accountNumber || '',
    ifscCode: data.ifscCode || '',
    bankName: data.bankName || '',
    notes: data.notes,
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
  };
}
